import { Suspense, useMemo, type ReactElement } from 'react';
import { LazyWhiteboardShell } from 'agentable-canvas/whiteboard';
import { OptionalCopilotKitBridge } from './components/canvas/OptionalCopilotKitBridge';
import { useCanvasSession } from './contexts/CanvasSessionContext';
import { CANVAS_STUDIO_PANEL_REGISTRY } from './lib/canvas-panel-registry';
import { getEnvStatus } from './env';

interface CanvasPersonaConfig {
  systemPrompt: string;
  assistantName?: string;
  tenantTitle?: string;
  tokenEndpoint?: string;
  chatProxyUrl?: string;
}

/**
 * Resolve the keyless text-chat proxy URL. Text `generateContent` cannot use
 * the Live voice token, so route it through landing-editor's server proxy.
 * Prefer an explicit override, else derive from the agents API origin.
 */
function resolveChatProxyUrl(): string | undefined {
  const explicit = (import.meta.env.VITE_LANDI_CHAT_PROXY_URL ?? '').trim();
  if (explicit) {
    return explicit;
  }
  const agentsUrl = (
    import.meta.env.VITE_LANDI_AGENTS_URL ??
    import.meta.env.VITE_LANDING_EDITOR_API_ORIGIN ??
    ''
  ).trim();
  if (!agentsUrl) {
    return undefined;
  }
  return `${agentsUrl.replace(/\/$/, '')}/api/ai/gemini/chat`;
}

interface CanvasHostConfig {
  tenant?: string;
  persona?: CanvasPersonaConfig;
}

function CanvasLoading(): ReactElement {
  return (
    <div className="canvas-host__surface" data-testid="canvas-loading">
      <p className="canvas-host__subtitle">Loading canvas…</p>
    </div>
  );
}

/** Full-viewport whiteboard host — edge-to-edge tldraw substrate (Stage 03). */
export function CanvasHost(): ReactElement {
  const env = getEnvStatus();
  const { session } = useCanvasSession();
  const tokenMintUrl = import.meta.env.VITE_TOKEN_MINT_URL ?? '';
  const chatProxyUrl = resolveChatProxyUrl();
  const persistenceScope = session.siteId ?? undefined;

  const config = useMemo<CanvasHostConfig>(
    () => ({
      tenant: 'landi-canvas-studio',
      persona: {
        systemPrompt:
          'You are Landi Canvas Studio — an AI assistant for building and editing landing pages on an infinite canvas.',
        assistantName: 'Landi',
        tenantTitle: 'Canvas Studio',
        tokenEndpoint: tokenMintUrl || undefined,
        chatProxyUrl,
      },
    }),
    [tokenMintUrl, chatProxyUrl],
  );

  if (!env.hasTokenMintUrl) {
    return (
      <main className="canvas-host" data-testid="canvas-host">
        <div className="canvas-host__surface">
          <p className="canvas-host__title">Landi Canvas Studio</p>
          <p className="canvas-host__subtitle">Configure VITE_TOKEN_MINT_URL in .env.local</p>
        </div>
      </main>
    );
  }

  return (
    <main className="canvas-host canvas-host--embedded" data-testid="canvas-host">
      <OptionalCopilotKitBridge>
        <Suspense fallback={<CanvasLoading />}>
          <LazyWhiteboardShell
            config={config}
            panels={CANVAS_STUDIO_PANEL_REGISTRY}
            layout="infinite-panels"
            darkCanvas
            hideTopBar
            openChatOnMount={false}
            enableSiteActionsTool
            enableSiteContextToolbar
            persistenceScope={persistenceScope}
          />
        </Suspense>
      </OptionalCopilotKitBridge>
    </main>
  );
}
